import { Injectable, inject } from '@angular/core';
import { ScanStatusMonitorService } from './scan-status-monitor.service';
import { ScanCompletionNotificationService } from './scan-completion-notification.service';
import { AlertNotificationService } from './alert-notification.service';
import { TenableFirestoreService } from '../tenable/service/tenable-firestore.service';
import { interval, Subscription } from 'rxjs';
import { LoggingService } from './logging.service';

/**
 * Configuration interface for the scan notification integration system
 *
 * @interface ScanNotificationConfig
 *
 * @property {boolean} enableDesktopNotifications - Enable browser desktop notifications for scan events
 * @property {boolean} enableInAppNotifications - Enable in-application notification display
 * @property {boolean} autoNavigateToCompletedScans - Automatically navigate to scan results when completed
 * @property {number} monitoringIntervalMinutes - Frequency in minutes for system health checks and heartbeat monitoring
 * @property {boolean} enableBulkNotifications - Enable processing multiple notifications in batches for performance
 *
 * @description
 * Defines all configurable options for the scan notification system behavior.
 * Settings can be updated dynamically while the system is running through
 * the updateConfiguration method.
 *
 * @example
 * ```typescript
 * const config: ScanNotificationConfig = {
 *   enableDesktopNotifications: true,
 *   enableInAppNotifications: false,
 *   autoNavigateToCompletedScans: true,
 *   monitoringIntervalMinutes: 2,
 *   enableBulkNotifications: true
 * };
 * ```
 */
export interface ScanNotificationConfig {
  enableDesktopNotifications: boolean;
  enableInAppNotifications: boolean;
  autoNavigateToCompletedScans: boolean;
  monitoringIntervalMinutes: number;
  enableBulkNotifications: boolean;
}

/**
 * Integration service that coordinates scan monitoring and notifications
 *
 * This service:
 * - Manages the overall scan completion notification system
 * - Coordinates between Firebase monitoring and desktop notifications
 * - Provides a single point of control for the notification system
 * - Handles initialization, configuration, and lifecycle management
 * - Designed to be easily tested and configured
 */
@Injectable({
  providedIn: 'root',
})
export class ScanNotificationIntegrationService {
  private scanStatusMonitor = inject(ScanStatusMonitorService);
  private loggingService = inject(LoggingService);
  private scanNotifications = inject(ScanCompletionNotificationService);
  private alertService = inject(AlertNotificationService);
  private tenableFirestoreService = inject(TenableFirestoreService);

  private isRunning = false;
  private statusMonitorSubscription?: Subscription;
  private heartbeatSubscription?: Subscription;
  private config: ScanNotificationConfig;

  private readonly defaultConfig: ScanNotificationConfig = {
    enableDesktopNotifications: true,
    enableInAppNotifications: false,
    autoNavigateToCompletedScans: false,
    monitoringIntervalMinutes: 1,
    enableBulkNotifications: true,
  };

  constructor() {
    this.config = { ...this.defaultConfig };
  }

  /**
   * Initialize and start the complete scan notification system
   *
   * @param customConfig - Optional partial configuration to override default settings
   * @returns Promise<boolean> - Returns true if system started successfully, false otherwise
   *
   * @description
   * This method performs the following initialization steps:
   * 1. Validates system isn't already running
   * 2. Applies custom configuration if provided
   * 3. Initializes notification service with fallback handling
   * 4. Starts Firebase monitoring for scan status changes
   * 5. Configures notification listening based on user preferences
   * 6. Sets up monitoring heartbeat for system health
   *
   * @example
   * ```typescript
   * const started = await service.start({
   *   enableDesktopNotifications: true,
   *   monitoringIntervalMinutes: 2
   * });
   * if (started) {
   *   console.log('Notification system is running');
   * }
   * ```
   */
  async start(
    customConfig?: Partial<ScanNotificationConfig>,
  ): Promise<boolean> {
    if (this.isRunning) {
      return true;
    }

    // Apply custom configuration
    if (customConfig) {
      this.config = { ...this.config, ...customConfig };
    }

    try {
      // Step 1: Initialize notification service
      const notificationReady = await this.scanNotifications.initialize();

      if (!notificationReady && this.config.enableDesktopNotifications) {
        this.config.enableDesktopNotifications = false;
        this.config.enableInAppNotifications = true;
      }

      // Step 2: Start Firebase monitoring
      this.scanStatusMonitor.startMonitoring();

      // Step 3: Start notification listening
      this.scanNotifications.startListening({
        showDesktopNotification: this.config.enableDesktopNotifications,
        showInAppNotification: this.config.enableInAppNotifications,
        autoNavigateToScan: this.config.autoNavigateToCompletedScans,
      });

      // Step 4: Setup monitoring heartbeat
      this.setupHeartbeat();

      this.isRunning = true;

      return true;
    } catch (error) {
      this.loggingService.logError(
        'Failed to start scan notification system:',
        error,
      );
      this.stop(); // Cleanup on failure
      return false;
    }
  }

  /**
   * Stop the scan notification system and cleanup all resources
   *
   * @returns void
   *
   * @description
   * Performs complete system shutdown by:
   * 1. Stopping Firebase monitoring
   * 2. Stopping notification listening
   * 3. Unsubscribing from heartbeat monitoring
   * 4. Clearing all active subscriptions
   * 5. Setting system state to stopped
   *
   * This method is safe to call multiple times and performs proper cleanup
   * to prevent memory leaks.
   *
   * @example
   * ```typescript
   * service.stop();
   * console.log('System stopped:', !service.isSystemRunning());
   * ```
   */
  stop(): void {
    // Stop Firebase monitoring
    this.scanStatusMonitor.stopMonitoring();

    // Stop notification listening
    this.scanNotifications.stopListening();

    // Stop heartbeat
    if (this.heartbeatSubscription) {
      this.heartbeatSubscription.unsubscribe();
      this.heartbeatSubscription = undefined;
    }

    // Clear subscriptions
    if (this.statusMonitorSubscription) {
      this.statusMonitorSubscription.unsubscribe();
      this.statusMonitorSubscription = undefined;
    }

    this.isRunning = false;
  }

  /**
   * Check if the notification system is currently running
   *
   * @returns boolean - True if system is active and monitoring, false otherwise
   *
   * @description
   * Returns the current operational state of the notification system.
   * This indicates whether the system has been started and is actively
   * monitoring for scan completions and sending notifications.
   *
   * @example
   * ```typescript
   * if (service.isSystemRunning()) {
   *   console.log('System is monitoring scans');
   * } else {
   *   await service.start();
   * }
   * ```
   */
  isSystemRunning(): boolean {
    return this.isRunning;
  }

  /**
   * Get a copy of the current notification system configuration
   *
   * @returns ScanNotificationConfig - Complete configuration object with all current settings
   *
   * @description
   * Returns a deep copy of the current configuration to prevent external
   * modification. Includes all notification preferences, monitoring intervals,
   * and feature flags.
   *
   * Configuration includes:
   * - enableDesktopNotifications: Desktop notification permission status
   * - enableInAppNotifications: In-app notification display setting
   * - autoNavigateToCompletedScans: Auto-navigation to completed scans
   * - monitoringIntervalMinutes: Heartbeat monitoring frequency
   * - enableBulkNotifications: Bulk notification processing setting
   *
   * @example
   * ```typescript
   * const config = service.getConfiguration();
   * console.log('Desktop notifications:', config.enableDesktopNotifications);
   * console.log('Monitoring interval:', config.monitoringIntervalMinutes);
   * ```
   */
  getConfiguration(): ScanNotificationConfig {
    return { ...this.config };
  }

  /**
   * Update notification system configuration while running
   *
   * @param updates - Partial configuration object with settings to update
   * @returns void
   *
   * @description
   * Dynamically updates system configuration without requiring a restart.
   * Only provided properties will be updated, existing settings remain unchanged.
   * If the system is currently running, changes are applied immediately:
   *
   * - Notification settings: Restarts notification listeners with new preferences
   * - Monitoring interval: Restarts heartbeat timer with new frequency
   * - Other settings: Applied on next system cycle
   *
   * @example
   * ```typescript
   * // Enable desktop notifications and change monitoring interval
   * service.updateConfiguration({
   *   enableDesktopNotifications: true,
   *   monitoringIntervalMinutes: 5
   * });
   *
   * // Disable auto-navigation only
   * service.updateConfiguration({
   *   autoNavigateToCompletedScans: false
   * });
   * ```
   */
  updateConfiguration(updates: Partial<ScanNotificationConfig>): void {
    const oldConfig = { ...this.config };
    this.config = { ...this.config, ...updates };

    // Apply changes if system is running
    if (this.isRunning) {
      this.applyConfigurationChanges(oldConfig, this.config);
    }
  }

  /**
   * Get comprehensive system health status and diagnostics
   *
   * @returns Object containing detailed system health information
   * @returns {boolean} isRunning - Whether the main system is active
   * @returns {boolean} monitoringActive - Firebase monitoring status
   * @returns {boolean} notificationListening - Notification listener status
   * @returns {boolean} desktopNotificationsAvailable - Browser notification permission
   * @returns {Date|null} lastHeartbeat - Timestamp of last system health check
   *
   * @description
   * Provides real-time diagnostics for troubleshooting notification issues.
   * Each component is checked independently to identify specific failure points.
   *
   * Health indicators:
   * - isRunning: Overall system operational status
   * - monitoringActive: Firebase scan status monitoring
   * - notificationListening: Active listening for scan events
   * - desktopNotificationsAvailable: Browser permission status
   * - lastHeartbeat: System responsiveness indicator
   *
   * @example
   * ```typescript
   * const health = service.getSystemHealth();
   *
   * if (!health.monitoringActive) {
   *   console.error('Firebase monitoring is down');
   * }
   *
   * if (!health.desktopNotificationsAvailable) {
   *   console.warn('Desktop notifications not permitted');
   * }
   *
   * console.log('System status:', health);
   * ```
   */
  getSystemHealth(): {
    isRunning: boolean;
    monitoringActive: boolean;
    notificationListening: boolean;
    desktopNotificationsAvailable: boolean;
    lastHeartbeat: Date | null;
  } {
    return {
      isRunning: this.isRunning,
      monitoringActive: this.scanStatusMonitor.isCurrentlyMonitoring(),
      notificationListening: this.scanNotifications.isCurrentlyListening(),
      desktopNotificationsAvailable:
        this.scanNotifications.canShowDesktopNotifications(),
      lastHeartbeat: new Date(), // Would track actual heartbeat in production
    };
  }

  /**
   * Force a manual scan status check for testing and debugging
   *
   * @returns void
   *
   * @description
   * Manually triggers an immediate scan status check outside of the normal
   * monitoring schedule. Useful for:
   * - Testing notification system responsiveness
   * - Debugging scan detection issues
   * - Forcing immediate processing of recent scan completions
   * - Development and QA testing scenarios
   *
   * Requires the system to be running. In production, this would trigger
   * the Azure Function to perform an immediate scan status poll.
   *
   * @throws Logs warning if system is not currently running
   *
   * @example
   * ```typescript
   * // Trigger immediate scan check for testing
   * service.triggerManualScanCheck();
   *
   * // Check after starting system
   * if (service.isSystemRunning()) {
   *   service.triggerManualScanCheck();
   * } else {
   *   console.log('Start system first');
   * }
   * ```
   */
  triggerManualScanCheck(): void {
    if (!this.isRunning) {
      return;
    }

    // This would trigger the Azure Function manually in a real implementation
  }

  /**
   * Setup monitoring heartbeat to ensure system responsiveness
   *
   * @private
   * @returns void
   *
   * @description
   * Establishes a recurring timer that performs system health checks
   * at intervals defined by the monitoring configuration. The heartbeat:
   *
   * - Monitors component health (Firebase, notifications)
   * - Detects system failures and degradation
   * - Logs health status for debugging
   * - Can trigger automatic recovery (future enhancement)
   *
   * Heartbeat frequency is controlled by `monitoringIntervalMinutes` config.
   * Previous heartbeat subscriptions are automatically cleaned up when
   * configuration changes.
   *
   * @internal Used internally during system startup
   */
  private setupHeartbeat(): void {
    const heartbeatInterval = this.config.monitoringIntervalMinutes * 60 * 1000; // Convert to milliseconds

    this.heartbeatSubscription = interval(heartbeatInterval).subscribe(() => {
      this.performHeartbeat();
    });
  }

  /**
   * Perform system health check and log any issues
   *
   * @private
   * @returns void
   *
   * @description
   * Executes a comprehensive health check of all system components:
   *
   * - Firebase monitoring connectivity and status
   * - Notification listener active state
   * - System responsiveness indicators
   *
   * Issues are logged as warnings for monitoring and debugging.
   * Future enhancements may include automatic recovery attempts
   * for common failure scenarios.
   *
   * Called periodically by the heartbeat timer to ensure
   * continuous system monitoring.
   *
   * @internal Called by heartbeat monitoring system
   */
  private performHeartbeat(): void {
    const health = this.getSystemHealth();

    // Check for system health issues
    if (!health.monitoringActive) {
      // Could attempt restart here
    }

    if (!health.notificationListening) {
      // Could attempt restart here
    }
  }

  /**
   * Apply configuration changes while system is running
   *
   * @private
   * @param oldConfig - Previous configuration state
   * @param newConfig - New configuration to apply
   * @returns void
   *
   * @description
   * Intelligently applies configuration changes without full system restart.
   * Compares old and new configurations to determine which components
   * need to be restarted:
   *
   * Notification settings changes:
   * - enableDesktopNotifications
   * - enableInAppNotifications
   * - autoNavigateToCompletedScans
   * → Restarts notification listeners with new settings
   *
   * Monitoring interval changes:
   * - monitoringIntervalMinutes
   * → Restarts heartbeat timer with new frequency
   *
   * This approach minimizes service disruption while ensuring
   * configuration changes take effect immediately.
   *
   * @internal Used by updateConfiguration method
   */
  private applyConfigurationChanges(
    oldConfig: ScanNotificationConfig,
    newConfig: ScanNotificationConfig,
  ): void {
    // Restart notification listening if settings changed
    if (
      oldConfig.enableDesktopNotifications !==
        newConfig.enableDesktopNotifications ||
      oldConfig.enableInAppNotifications !==
        newConfig.enableInAppNotifications ||
      oldConfig.autoNavigateToCompletedScans !==
        newConfig.autoNavigateToCompletedScans
    ) {
      this.scanNotifications.stopListening();
      this.scanNotifications.startListening({
        showDesktopNotification: newConfig.enableDesktopNotifications,
        showInAppNotification: newConfig.enableInAppNotifications,
        autoNavigateToScan: newConfig.autoNavigateToCompletedScans,
      });
    }

    // Restart heartbeat if interval changed
    if (
      oldConfig.monitoringIntervalMinutes !==
      newConfig.monitoringIntervalMinutes
    ) {
      if (this.heartbeatSubscription) {
        this.heartbeatSubscription.unsubscribe();
      }
      this.setupHeartbeat();
    }
  }

  /**
   * Initialize alert service with user preferences and setup scan monitoring
   *
   * @param userId - Unique identifier for the user to load their alert preferences
   * @returns Promise<boolean> - Returns true if initialization successful, false otherwise
   *
   * @description
   * Initializes the alert notification service for sending scan-related alerts
   * including completion notifications and critical vulnerability warnings.
   *
   * Performs the following operations:
   * 1. Loads user-specific alert preferences from storage
   * 2. Configures alert delivery channels (email, desktop, in-app)
   * 3. Sets up scan event listeners for automatic alert triggering
   * 4. Validates alert service connectivity and permissions
   *
   * After successful initialization, the service will automatically:
   * - Send scan completion alerts with vulnerability summaries
   * - Send critical vulnerability alerts for high-risk findings
   * - Respect user notification preferences and delivery settings
   *
   * @throws Logs error if alert service initialization fails
   *
   * @example
   * ```typescript
   * const userId = 'user-123';
   * const success = await service.initializeAlertService(userId);
   *
   * if (success) {
   *   console.log('Alert service ready for notifications');
   * } else {
   *   console.error('Failed to setup alert service');
   * }
   * ```
   */
  async initializeAlertService(userId: string): Promise<boolean> {
    try {
      const success = await this.alertService.initialize(userId);
      if (success) {
        this.setupScanAlertListeners();
      }
      return success;
    } catch (error) {
      this.loggingService.logError(
        'Failed to initialize alert service:',
        error,
      );
      return false;
    }
  }

  /**
   * Setup event listeners for scan status changes to trigger automatic alerts
   *
   * @private
   * @returns void
   *
   * @description
   * Establishes reactive listeners that automatically send appropriate alerts
   * when scan events occur. Monitors two main event streams:
   *
   * **Scan Completion Events:**
   * - Triggers when scans transition to 'completed' status
   * - Sends scan completion alerts with vulnerability summaries
   * - Includes total vulnerability counts by severity level
   * - Provides quick access links to scan results
   *
   * **Critical Vulnerability Events:**
   * - Monitors all completed scans for critical vulnerabilities
   * - Filters for severity 4 (Critical) or CVSS >= 9.0 vulnerabilities
   * - Sends individual alerts for each critical finding
   * - Includes CVE details, affected hosts, and remediation priority
   *
   * Alert processing includes error handling to ensure system stability
   * and prevent alert storms. Failed alerts are logged for debugging.
   *
   * @internal Called during alert service initialization
   */
  private setupScanAlertListeners(): void {
    // Listen for scan completions
    this.scanStatusMonitor
      .getCompletionChanges()
      .subscribe(async (completions) => {
        for (const completion of completions) {
          try {
            await this.alertService.sendScanCompletionAlert({
              id: completion.scanId,
              name: completion.name,
              currentCompanyId: completion.company,
              vulnerabilitySummary: await this.getScanVulnerabilitySummary(
                completion.scanId,
              ),
            });
          } catch (error) {
            this.loggingService.logError(
              'Failed to send scan completion alert:',
              error,
            );
          }
        }
      });

    // Listen for all scan status changes to detect critical vulnerabilities
    this.scanStatusMonitor.getScanStatusChanges().subscribe(async (changes) => {
      for (const change of changes) {
        if (change.currentStatus === 'completed') {
          try {
            // Check for critical vulnerabilities in completed scan
            const criticalVulns = await this.getCriticalVulnerabilities(
              change.scanId,
            );

            for (const vuln of criticalVulns) {
              await this.alertService.sendCriticalVulnerabilityAlert({
                cve_id: vuln.cve_id || 'Unknown',
                cvss_score: vuln.cvss_score || 0,
                hostname: vuln.hostname || 'Unknown',
                description:
                  vuln.description || 'Critical vulnerability detected',
                scanId: change.scanId,
                currentCompanyId: change.company,
              });
            }
          } catch (error) {
            this.loggingService.logError(
              'Failed to process critical vulnerability alerts:',
              error,
            );
          }
        }
      }
    });
  }

  /**
   * Retrieve and categorize vulnerability summary for a completed scan
   *
   * @private
   * @param scanId - Unique identifier of the completed scan
   * @returns Promise<VulnerabilitySummary> - Object containing vulnerability counts by severity
   * @returns {number} critical - Count of critical vulnerabilities (severity 4)
   * @returns {number} high - Count of high severity vulnerabilities (severity 3)
   * @returns {number} medium - Count of medium severity vulnerabilities (severity 2)
   * @returns {number} low - Count of low/info vulnerabilities (severity 0-1)
   * @returns {number} total - Total vulnerability count across all severities
   *
   * @description
   * Queries Firestore for scan details and processes vulnerability data to create
   * a categorized summary for inclusion in scan completion alerts.
   *
   * **Severity Mapping (Tenable → Standard):**
   * - Severity 4 → Critical (CVSS 9.0-10.0)
   * - Severity 3 → High (CVSS 7.0-8.9)
   * - Severity 2 → Medium (CVSS 4.0-6.9)
   * - Severity 0-1 → Low/Info (CVSS 0.0-3.9)
   *
   * **Data Processing:**
   * - Handles both array and object vulnerability data structures
   * - Provides graceful fallback for missing or invalid data
   * - Logs processing results for monitoring and debugging
   *
   * Returns zero counts if scan data unavailable or processing fails.
   *
   * @throws Returns empty summary object on error, logs error details
   *
   * @example
   * ```typescript
   * const summary = await getScanVulnerabilitySummary('scan-123');
   * console.log(`Found ${summary.critical} critical vulnerabilities`);
   * // Output: { critical: 2, high: 5, medium: 12, low: 8, total: 27 }
   * ```
   *
   * @internal Used by scan completion alert generation
   */
  private getScanVulnerabilitySummary(scanId: string): Promise<any> {
    return new Promise((resolve, reject) => {
      // Query scan details from Firestore to get vulnerability data
      this.tenableFirestoreService.getScanDetails(scanId).subscribe({
        next: (scanDetails: any) => {
          if (!scanDetails || !scanDetails.vulnerabilities) {
            resolve({ critical: 0, high: 0, medium: 0, low: 0, total: 0 });
            return;
          }

          // Convert vulnerabilities object to array if needed
          const vulnerabilities = Array.isArray(scanDetails.vulnerabilities)
            ? scanDetails.vulnerabilities
            : Object.values(scanDetails.vulnerabilities);

          // Initialize counters
          const summary = { critical: 0, high: 0, medium: 0, low: 0, total: 0 };

          // Categorize vulnerabilities by severity
          vulnerabilities.forEach((vuln: any) => {
            summary.total++;

            // Map Tenable severity levels to standard CVSS ranges
            // Tenable: 0=Info, 1=Low, 2=Medium, 3=High, 4=Critical
            switch (vuln.severity) {
              case 4:
                summary.critical++;
                break;
              case 3:
                summary.high++;
                break;
              case 2:
                summary.medium++;
                break;
              case 1:
              case 0:
              default:
                summary.low++;
                break;
            }
          });

          resolve(summary);
        },
        error: (error: any) => {
          this.loggingService.logError(
            'Error getting vulnerability summary for scan:',
            error,
          );
          reject(error);
        },
      });
    });
  }

  /**
   * Retrieve and transform critical vulnerabilities from a completed scan
   *
   * @private
   * @param scanId - Unique identifier of the completed scan to analyze
   * @returns Promise<CriticalVulnerability[]> - Array of critical vulnerabilities formatted for alerts
   *
   * @description
   * Queries scan details from Firestore and identifies critical security vulnerabilities
   * that require immediate attention. Filters and transforms vulnerability data
   * into a standardized format suitable for alert notifications.
   *
   * **Critical Vulnerability Criteria:**
   * - Tenable severity level 4 (Critical)
   * - CVSS score >= 9.0 (if available)
   * - High impact security findings requiring urgent remediation
   *
   * **Returned Vulnerability Object Properties:**
   * - `cve_id`: CVE identifier or plugin name for vulnerability identification
   * - `cvss_score`: CVSS score (defaults to 9.0 for severity 4 vulnerabilities)
   * - `hostname`: Affected host or scan target name
   * - `description`: Human-readable vulnerability description
   * - `scanId`: Source scan identifier for traceability
   * - `currentCompanyId`: Company/organization identifier
   * - `plugin_id`: Tenable plugin identifier
   * - `plugin_family`: Vulnerability category/family
   * - `severity`: Numeric severity level (0-4)
   * - `count`: Number of instances found
   *
   * **Data Processing:**
   * - Handles both array and object vulnerability data structures
   * - Provides safe defaults for missing fields
   * - Logs critical vulnerability detection for monitoring
   * - Returns empty array for graceful error handling
   *
   * Used by the alert system to send immediate notifications for
   * high-priority security findings that require urgent attention.
   *
   * @throws Returns empty array on error, logs error details for debugging
   *
   * @example
   * ```typescript
   * const criticalVulns = await getCriticalVulnerabilities('scan-456');
   *
   * criticalVulns.forEach(vuln => {
   *   console.log(`Critical: ${vuln.cve_id} (CVSS: ${vuln.cvss_score})`);
   *   console.log(`Host: ${vuln.hostname}`);
   *   console.log(`Description: ${vuln.description}`);
   * });
   *
   * // Example output:
   * // Critical: CVE-2023-12345 (CVSS: 9.8)
   * // Host: web-server-01
   * // Description: Remote code execution vulnerability
   * ```
   *
   * @internal Used by critical vulnerability alert generation
   */
  private async getCriticalVulnerabilities(scanId: string): Promise<any[]> {
    return new Promise((resolve, reject) => {
      // Query scan details from Firestore to get vulnerability data
      this.tenableFirestoreService.getScanDetails(scanId).subscribe({
        next: (scanDetails: any) => {
          try {
            if (!scanDetails || !scanDetails.vulnerabilities) {
              resolve([]);
              return;
            }

            // Convert vulnerabilities object to array if needed
            const vulnerabilities = Array.isArray(scanDetails.vulnerabilities)
              ? scanDetails.vulnerabilities
              : Object.values(scanDetails.vulnerabilities);

            // Filter for critical vulnerabilities (severity 4 in Tenable, or CVSS >= 9.0)
            const criticalVulns = vulnerabilities.filter((vuln: any) => {
              // Tenable severity 4 = Critical
              if (vuln.severity === 4) {
                return true;
              }

              // Also check for CVSS score >= 9.0 if available
              if (vuln.cvss_score && vuln.cvss_score >= 9.0) {
                return true;
              }

              return false;
            });

            // Resolve company identifier (support both new and legacy field names)
            const currentCompanyIdentifier =
              scanDetails.currentCompanyId || scanDetails.currentCompanyId || undefined;

            // Transform to standardized format for alerts (currentCompanyId here is informational; the sending call overrides with authoritative value)
            const transformedVulns = criticalVulns.map((vuln: any) => ({
              cve_id:
                vuln.cve_id || vuln.plugin_name || `Plugin-${vuln.plugin_id}`,
              cvss_score: vuln.cvss_score || (vuln.severity === 4 ? 9.0 : 0),
              hostname:
                scanDetails.info?.name || scanDetails.target || 'Unknown Host',
              description:
                vuln.description ||
                vuln.plugin_name ||
                `Critical vulnerability detected in plugin ${vuln.plugin_id}`,
              scanId: scanId,
              currentCompanyId: currentCompanyIdentifier || 'unknown',
              plugin_id: vuln.plugin_id,
              plugin_family: vuln.plugin_family,
              severity: vuln.severity,
              count: vuln.count || 1,
            }));

            resolve(transformedVulns);
          } catch (error) {
            this.loggingService.logError(
              `Error processing critical vulnerabilities for scan ${scanId}:`,
              error,
            );
            reject(error);
          }
        },
        error: (error: any) => {
          this.loggingService.logError(
            `Error retrieving critical vulnerabilities for scan ${scanId}:`,
            error,
          );
          reject(error);
        },
      });
    });
  }

  /**
   * Send a test alert for development and system validation
   *
   * @returns Promise<void> - Resolves when test alert is sent successfully
   *
   * @description
   * Sends a predefined test alert through the alert notification system
   * to validate proper configuration and delivery. Useful for:
   *
   * - **Development Testing**: Verify alert system integration
   * - **System Validation**: Confirm notification delivery channels
   * - **User Setup**: Test notification preferences and permissions
   * - **Debugging**: Troubleshoot alert delivery issues
   *
   * **Test Alert Contains:**
   * - Mock scan completion data with realistic vulnerability summary
   * - Sample vulnerability counts across all severity levels
   * - Test company and scan identifiers
   * - All standard alert metadata and formatting
   *
   * **Test Data Structure:**
   * ```
   * Scan: "Test Security Scan" (ID: test-scan-123)
   * Company: test-company
   * Vulnerabilities: 2 Critical, 5 High, 12 Medium, 8 Low (27 Total)
   * ```
   *
   * Success/failure is logged for monitoring. Does not affect
   * production scan monitoring or real alert processing.
   *
   * @throws Logs error details if test alert fails to send
   *
   * @example
   * ```typescript
   * // Test alert system during setup
   * try {
   *   await service.sendTestAlert();
   *   console.log('Alert system working correctly');
   * } catch (error) {
   *   console.error('Alert system configuration issue');
   * }
   *
   * // Validate user notification preferences
   * await service.sendTestAlert();
   * // User should receive test notification if properly configured
   * ```
   */
  async sendTestAlert(): Promise<void> {
    try {
      await this.alertService.sendScanCompletionAlert({
        id: 'test-scan-123',
        name: 'Test Security Scan',
        currentCompanyId: 'test-company',
        vulnerabilitySummary: {
          critical: 2,
          high: 5,
          medium: 12,
          low: 8,
          total: 27,
        },
      });
    } catch (error) {
      this.loggingService.logError('Failed to send test alert:', error);
    }
  }
}
